/**
 * Character editor with version history and a diff view (§7.2).
 *
 * Versions are immutable and pinned by rentals, so "what changed between v3 and
 * v4" is a question a renter or an owner will actually ask after the fact. The
 * diff view is mobile-exempt (§7.8): on a phone it says so rather than rendering
 * two columns of shredded text.
 */

import { useState } from 'react';
import { useApi, useMutation } from '../../hooks/useApi';
import { useBotOutlet } from '../botOutlet';
import type { CharacterProfile, Page } from '../../api/types';
import { useLayout } from '../../hooks/useMediaQuery';
import { Gate } from '../../components/Gate';
import { Card, Field, LiveRegion, Loading } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { diffLines } from '../../lib/diff';
import { localDateTime } from '../../lib/format';
import { routes } from '../../api/routes';

function DiffView({ before, after }: { before: string; after: string }) {
  const ops = diffLines(before, after);
  return (
    <pre className="diff" aria-label="Differences between versions">
      {ops.map((op, i) => (
        <span key={i} className={`diff-${op.kind}`}>
          {op.kind === 'add' ? '+' : op.kind === 'remove' ? '-' : ' '} {op.text}
          {'\n'}
        </span>
      ))}
    </pre>
  );
}

export function CharacterEditor() {
  const { bot, caps } = useBotOutlet();
  const layout = useLayout();
  const current = useApi<CharacterProfile>(routes.bots.character(bot.id));
  const history = useApi<Page<CharacterProfile>>(routes.bots.characterVersions(bot.id), {
    query: { limit: 20 },
  });
  const save = useMutation<Record<string, unknown>, CharacterProfile>(routes.bots.character(bot.id), {
    method: 'PUT',
  });

  const [draft, setDraft] = useState<Partial<CharacterProfile> | null>(null);
  const [compare, setCompare] = useState<{ a: number; b: number } | null>(null);

  if (current.initialLoading) return <Loading label="Loading the character" />;
  if (current.problem) return <ErrorView problem={current.problem} onRetry={current.reload} />;

  // A draft bot has no character version yet; that is a real state, not an error.
  const profile = current.data;
  if (!profile) {
    return (
      <Card title="Character">
        <EmptyState
          heading="No character has been written yet"
          body="A bot is a persona: the description here is what every generation is built on."
        />
      </Card>
    );
  }

  const value = { ...profile, ...(draft ?? {}) };
  const versions = history.data?.data ?? [];
  const left = compare ? versions.find((v) => v.version === compare.a) : null;
  const right = compare ? versions.find((v) => v.version === compare.b) : null;

  const locked = new Set(profile.locked_fields);

  return (
    <div className="character">
      <Gate
        caps={caps}
        capability="edit_character"
        fallback={
          <Card title={`Character (v${profile.version})`}>
            <p className="preview">{profile.description}</p>
            <p className="muted">You can read this character but not change it.</p>
          </Card>
        }
      >
        {(disabled) => (
          <Card title={`Character (v${profile.version})`}>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if ((await save.run({ ...value })) !== null) {
                  setDraft(null);
                  current.reload();
                  history.reload();
                }
              }}
            >
              <fieldset disabled={disabled}>
                <Field
                  id="ch-desc"
                  label="Description"
                  hint={locked.has('description') ? 'Locked by the owner for this rental.' : undefined}
                >
                  <textarea
                    id="ch-desc"
                    className="textarea"
                    rows={8}
                    readOnly={locked.has('description')}
                    value={value.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  />
                </Field>
                <Field id="ch-tone" label="Tone">
                  <input
                    id="ch-tone"
                    className="input"
                    readOnly={locked.has('tone')}
                    value={value.tone}
                    onChange={(e) => setDraft({ ...draft, tone: e.target.value })}
                  />
                </Field>
                <Field id="ch-allow" label="Topics to stay on" hint="Comma separated.">
                  <input
                    id="ch-allow"
                    className="input"
                    value={value.topics_allow.join(', ')}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        topics_allow: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      })
                    }
                  />
                </Field>
                <Field id="ch-deny" label="Topics to avoid">
                  <input
                    id="ch-deny"
                    className="input"
                    value={value.topics_deny.join(', ')}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        topics_deny: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      })
                    }
                  />
                </Field>
                {save.problem ? <InlineError problem={save.problem} /> : null}
                <button type="submit" className="btn btn-primary" disabled={disabled || save.pending}>
                  {save.pending ? 'Saving…' : 'Save as a new version'}
                </button>
                {save.data ? <LiveRegion>Saved as v{save.data.version}.</LiveRegion> : null}
              </fieldset>
            </form>
          </Card>
        )}
      </Gate>

      <Card title="Version history">
        {history.initialLoading ? <Loading label="Loading versions" /> : null}
        <ul className="version-list">
          {versions.map((v) => (
            <li key={v.version} className="version">
              <span>
                v{v.version} · {localDateTime(v.created_at)} · @{v.created_by_handle}
              </span>
              {v.version > 1 ? (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setCompare({ a: v.version - 1, b: v.version })}
                >
                  Compare with v{v.version - 1}
                </button>
              ) : null}
            </li>
          ))}
        </ul>

        {compare ? (
          layout === 'compact' ? (
            <p className="desktop-only-note">
              The version diff needs a wider screen. Open this page on a laptop or desktop to compare
              v{compare.a} with v{compare.b}.
            </p>
          ) : left && right ? (
            <>
              <h3>
                v{compare.a} to v{compare.b}
              </h3>
              <DiffView before={left.description} after={right.description} />
            </>
          ) : (
            <p className="muted">Those versions are outside the loaded history.</p>
          )
        ) : null}
      </Card>
    </div>
  );
}
